import { Button, Stack, Typography, Theme, useMediaQuery, Skeleton } from "@mui/material";
import CustomDialog from "global/components/CustomDialog/CustomDialog";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import { MuiOtpInput } from "mui-one-time-password-input";
import { useState, useEffect } from "react";
import { resendVerificationCode, verifySender } from "../SendersService";

interface CustomProps {
  isVisible: boolean;
  handleDialogClose: Function;
  filters: any;
  setFilters: Function;
  senderEmail: string;
  senderName: string;
  senderId: number;
  openedFrom?: "add" | "verify";
}

const SenderVerification = (props: CustomProps) => {
  const {
    isVisible,
    handleDialogClose,
    filters,
    setFilters,
    senderEmail,
    senderName,
    senderId,
  } = props;

  const [code, setCode] = useState<string>("");
  const [timer, setTimer] = useState(60);
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));
  const [processing, setProcessing] = useState<boolean>(false);
  const [resending, setResending] = useState<boolean>(false);

  useEffect(() => {
    if (isVisible) {
      setTimer(props.openedFrom === "verify" ? 0 : 60);
    }
  }, [isVisible]);

  useEffect(() => {
    if (timer > 0) {
      const countdown = setTimeout(() => setTimer(timer - 1), 1000);
      return () => clearTimeout(countdown);
    }
  }, [timer]);

  const getVerifySender = async () => {
    try {
      setProcessing(true);
      const res = await verifySender(senderId, code);
      openSuccessNotification(res.message);
      setProcessing(false);
      handleDialogClose();
      setFilters({
        ...filters,
        page: 1,
      });
    } catch (error: any) {
      setProcessing(false);
      setCode("");
      handleErrorMessage(error);
    }
  };

  const getResendVerificationCode = async () => {
    try {
      setResending(true);
      setTimer(60);
      setCode("");
      const res = await resendVerificationCode(senderId);
      openSuccessNotification(res.message);
      setResending(false);
    } catch (error: any) {
      setResending(false);
      handleErrorMessage(error);
    }
  };

  const getBody = () => {
    return (
      <Stack spacing={3}>
        <Typography
          variant="h4"
          color="text.primary"
          fontWeight={700}
          mb={1.5}
          sx={{ wordBreak: "break-word" }}
        >
          Verify the new sender {senderName}
        </Typography>
        <Typography variant="body2" color="text.primary" mb={4}>
          Before you can use <strong>{senderName}</strong> as a sender, we must
          verify its email address. We have sent you a verification code by
          email at <strong>{senderEmail}</strong>
        </Typography>

        <Typography
          variant="body1"
          color="text.primary"
          fontWeight={600}
          mb={1}
        >
          Enter the verification code you received:
        </Typography>
        <Stack direction="row" spacing={1} mb={3} alignItems="center">
          {resending ? (
            <Stack direction="row" spacing={isDesktop ? 2.5 : 1}>
              {[...Array(4)].map((_, i) => (
                <Skeleton
                  key={i}
                  variant="circular"
                  width={isDesktop ? 52 : 32}
                  height={isDesktop ? 52 : 36}
                  sx={{ borderRadius: "4px" }}
                />
              ))}
            </Stack>
          ) : (
            <MuiOtpInput
              length={4}
              onChange={(value: any) => {
                setCode(value.replace(/[^0-9]/g, ""));
              }}
              sx={{
                "& .MuiFilledInput-input": {
                  p: isDesktop ? "14px" : "10px",
                  color: "text.primary",
                  fontWeight: 600,
                },
                gap: isDesktop ? "20px" : "8px",
                "& .MuiFilledInput-root": {
                  width: isDesktop ? "50px" : "32px",
                  minWidth: isDesktop ? "52px" : "32px",
                },
              }}
              value={code}
              TextFieldsProps={{
                type: "number",
                inputProps: {
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  style: {
                    MozAppearance: "textfield",
                  },
                },
                sx: {
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                  },
                  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                    {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                },
              }}
            />
          )}
        </Stack>

        <Stack direction="row" alignItems="center">
          <Typography variant="body2" color="text.primary" fontWeight={600}>
            Did not receive the code?{" "}
            <span>
              <Button
                variant="text"
                disabled={timer > 0}
                onClick={() => getResendVerificationCode()}
                sx={{
                  m: "0px !important",
                  p: "0px !important",
                  color: "primary.main",
                }}
              >
                {timer > 0
                  ? `Resend verification code (${timer}s)`
                  : "Resend verification code"}
              </Button>
            </span>
          </Typography>
        </Stack>

        <Stack direction="row" spacing={2} justifyContent="center">
          <Button variant="outlined" onClick={() => handleDialogClose()}>
            Do this later
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={processing || code.length < 4}
            onClick={() => getVerifySender()}
          >
            Verify sender
          </Button>
        </Stack>
      </Stack>
    );
  };

  return (
    <CustomDialog
      isDialogOpen={isVisible}
      title={<></>}
      body={getBody()}
      handleDialogClose={() => handleDialogClose()}
      maxWidth="sm"
      closable
      closeButtonVisibility
      persistOnClickOutside
      persistOnEsc
    />
  );
};

export default SenderVerification;
